import { useEffect, useState } from "react";
import { Account, eventerConsoleApiClient, Invitation } from "../../../../api-client/EventerConsoleApiClient.ts";
import { LoadingControl } from "../../../../../appfw/loading/use-loading-control.tsx";

interface InvitationsProps {
    readonly account: Account;
    readonly loadingControl?: LoadingControl;
}

export interface InvitationsController {
    invitations: undefined | Invitation[];
    refresh: (abortController?: AbortController) => Promise<void>;
    remove: (invitationId: string) => Promise<void>;
}

export function useInvitations(props: InvitationsProps): InvitationsController {

    const [invitations, setInvitations] = useState<undefined | Invitation[]>(undefined)

    const _refresh = async (abortController?: AbortController) => {
        if (props.account.role !== "owner") {
            // not allowed to list invitations
            return
        }
        try {
            const fetchedInvitations = await eventerConsoleApiClient.listInvitations({ accountId: props.account.id }, { abortController })

            // sort by email
            const sorted = [...fetchedInvitations].sort((a, b) => a.email.localeCompare(b.email))

            // smart update: only update if changed
            const isDifferent =
                !invitations ||
                invitations.length !== sorted.length ||
                invitations.some((inv, i) => {
                    let fetchedInv = sorted[i]
                    return inv.id !== fetchedInv.id || inv.email !== fetchedInv.email || inv.invitedDate !== fetchedInv.invitedDate
                })
            if (isDifferent) {
                setInvitations(sorted)
            }
        } catch (error) {
            console.error("Failed to load invitations:", error);
        }
    }

    const refresh = async (abortController?: AbortController) => {
        if (props.loadingControl) {
            await props.loadingControl.withLoading(async () => _refresh(abortController))
        } else {
            await _refresh(abortController)
        }
    }

    const _remove = async (invitationId: string, abortController?: AbortController) => {
        await eventerConsoleApiClient.deleteInvitation({
            accountId: props.account.id,
            invitationId: invitationId
        }, { abortController })
        await _refresh()
    }

    const remove = async (invitationId: string) => {
        if (props.loadingControl) {
            await props.loadingControl.withLoading(async () => _remove(invitationId))
        } else {
            await _remove(invitationId)
        }
    }

    useEffect(() => {
        const abortController = new AbortController()

        refresh(abortController)

        return () => {
            abortController.abort()
        }
    }, [props.account.id])

    return { invitations, refresh, remove }
}